import { useMemo } from 'react';
import { ODataDsnsService } from '../services/ODataDsnsService';
import { useLogger } from '../context/useLogger';
import { useAccessToken } from './useAccessToken';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { FilterCondition } from '../types/filter.types';
import { DsnListResponse } from '../types/dsn.types';

export function useQueryDsns(
  filter: FilterCondition[],
  orderBy: string[],
  expand: string[],
  pageSize: number,
  currentPage: number,
): UseQueryResult<DsnListResponse, Error> {
  const REFETCH_INTERVAL_MS = 180_000; // 3 minutes
  const getAccessToken = useAccessToken();
  const logger = useLogger();

  const odataService = useMemo(
    () => new ODataDsnsService(getAccessToken, logger),
    [getAccessToken, logger],
  );

  const fetchDsns = (signal: AbortSignal) => {
    return odataService.fetchDsnList(filter, orderBy, expand, pageSize, currentPage, signal);
  };

  const queryKey = ['query-get', 'Dsns', filter, orderBy, expand, pageSize, currentPage];
  const queryResult = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchDsns(signal),
    throwOnError: true,
    enabled: filter.some(c => c.isEnabled),
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: true, // même si l'onglet est en arrière-plan
    staleTime: 30_000, // Considérer les données stales après 30s
  });

  return queryResult;
}
